import React, { useState, useEffect } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { InvitationForm } from './InvitationForm'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'

interface InvitationDialogProps {
  isOpen: boolean
  onClose: () => void
  invitation: UserService.Invitation | null
  prefilledData?: Partial<UserService.Invitation>
  onInvitationSaved?: () => void
  allAccessGroups?: string[]
  allUsers?: UserService.User[]
}

export const InvitationDialog: React.FC<InvitationDialogProps> = ({
  isOpen,
  onClose,
  invitation,
  prefilledData,
  onInvitationSaved,
  allAccessGroups = [],
  allUsers = []
}) => {
  const { t } = useTar()
  const [formData, setFormData] = useState<Partial<UserService.Invitation> & { sendEmails?: boolean }>({ sendEmails: true })
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Reset form data when invitation changes
  useEffect(() => {
    if (invitation) {
      setFormData({ ...invitation })
    } else {
      setFormData({
        email: prefilledData?.email || '',
        firstName: prefilledData?.firstName || '',
        lastName: prefilledData?.lastName || '',
        accessGroups: prefilledData?.accessGroups || [],
        validFor: prefilledData?.validFor || 7, // 7 days default
        status: (prefilledData?.status || 'active') as UserService.InvitationStatusEnum,
        sendEmails: true // Default to sending emails
      })
    }
    setSaveError(null)
    setIsSaving(false)
  }, [invitation, prefilledData])

  const handleInputChange = (field: keyof UserService.Invitation | 'sendEmails', value: string | number | string[] | boolean | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    setSaveError(null)

    // Basic validation
    if (!formData.email) {
      setSaveError('Email is required.')
      return
    }

    if (formData.role === UserService.UserRole.Partner) {
      formData.accessGroups = ['partner']
    } else {
      if (!formData.accessGroups || formData.accessGroups.length === 0) {
        setSaveError('At least one access group is required for regular invitations.')
        return
      }
    }

    try {
      setIsSaving(true)
      if (invitation) {
        // Update existing invitation
        await apis.userService.invitationTokenPut(invitation.id, formData as UserService.UpdateInvitationRequest)
      } else {
        // Create new invitation
        await apis.userService.invitationsPost(formData as UserService.CreateInvitationRequest)
      }

      // Call the callback to refresh parent component
      onInvitationSaved?.()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to save invitation:', error)
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to save invitation. Please try again.'
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const isReadOnly = !!(invitation && invitation.status !== 'active')

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSaving && !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-3xl lg:max-w-4xl sm:min-w-[500px] lg:min-w-[600px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onInteractOutside={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {invitation ? `${t('invitations.editInvitation')}: ${invitation.email}` : t('invitations.newInvitation')}
          </DialogTitle>
        </DialogHeader>

        <InvitationForm
          formData={formData}
          onChange={handleInputChange}
          onSave={handleSave}
          onCancel={onClose}
          isSaving={isSaving}
          saveError={saveError}
          isEditing={!!invitation}
          isReadOnly={isReadOnly}
          allAccessGroups={allAccessGroups}
          allUsers={allUsers}
        />
      </DialogContent>
    </Dialog>
  )
}
